import { Add, Close, Save } from '@mui/icons-material';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Theme,
  Typography,
  Box,
  ButtonGroup,
  Button,
  Divider
} from '@mui/material';
import { useQuery } from 'react-query';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC } from 'react';
import { Loader, SMBSelect } from '../../../../components';
import {
  IPlanDetails,
  IPlanDetailsPut,
  IDropdownResponse,
  IPlanStyleRes
} from '../../../../models';
import {
  putPlanDetails,
  getWallThicknesses,
  getShowerStyles,
  getBedBaths,
  getPlanStyles,
  getStairStyles
} from '../../../../fetch';

interface IDetails {
  details: IPlanDetails;
  isLoadingDetails: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchDetails: () => void;
}

const DetailsSchema = Yup.object().shape({
  planId: Yup.string(),
  cFootage: Yup.number().nullable(),
  cFootage1: Yup.number().nullable(),
  cFootage2: Yup.number().nullable(),
  sFootageBrick: Yup.number().nullable(),
  sFootageSiding: Yup.number().nullable(),
  sFootageMixed: Yup.number().nullable(),
  sFootage1: Yup.number().nullable(),
  sFootage2: Yup.number().nullable(),
  porchFootage: Yup.number().nullable(),
  garageFootage: Yup.number().nullable(),
  carportFootage: Yup.number().nullable(),
  deckFootage: Yup.number().nullable(),
  unfinishedFootage: Yup.number().nullable(),
  slabStructuralFootage: Yup.number().nullable(),
  slabNonStructuralFootage: Yup.number().nullable(),
  threeCarCFootage: Yup.number().nullable(),
  threeCarSFootageBrick: Yup.number().nullable(),
  threeCarSFootageMixed: Yup.number().nullable(),
  threeCarSFootageSiding: Yup.number().nullable(),
  threeCarSlabStructuralFootage: Yup.number().nullable(),
  threeCarGarageFootage: Yup.number().nullable(),
  planStyle: Yup.string().required('Required'),
  bedBath: Yup.string().required('Required'),
  isTwoStory: Yup.string(),
  isMasterUpstairs: Yup.string(),
  kitchenHasIsland: Yup.string(),
  stairStyle: Yup.string(),
  showerStyle: Yup.string().required('Required'),
  wallThickness: Yup.string(),
  salesDetails: Yup.string().max(2000, 'Max 2000 characters').notRequired(),
  widthFeet: Yup.number().nullable(),
  widthInches: Yup.number().nullable(),
  depthFeet: Yup.number().nullable(),
  depthInches: Yup.number().nullable(),
  ceilingHeightFirstFloor: Yup.number()
    .nullable()
    .typeError('Must be a number')
    .max(10000, 'Must be at most 10000')
    .test('is-decimal', 'Must be less than 10000', value => {
      if (value) {
        // Check if the whole number part is less than 10000
        const wholeNumberPart = value.toString().split('.')[0];
        return parseInt(wholeNumberPart, 10) <= 10000;
      }
      return true;
    }),
  ceilingHeightSecondFloor: Yup.number()
    .nullable()
    .typeError('Must be a number')
    .max(10000, 'Must be at most 10000')
    .test('is-decimal', 'Must be less than 10000', value => {
      if (value) {
        // Check if the whole number part is less than 10000
        const wholeNumberPart = value.toString().split('.')[0];
        return parseInt(wholeNumberPart, 10) <= 10000;
      }
      return true;
    }),
  ceilingHeightBasement: Yup.number()
    .nullable()
    .typeError('Must be a number')
    .max(10000, 'Must be at most 10000')
    .test('is-decimal', 'Must be less than 10000', value => {
      if (value) {
        // Check if the whole number part is less than 10000
        const wholeNumberPart = value.toString().split('.')[0];
        return parseInt(wholeNumberPart, 10) <= 10000;
      }
      return true;
    }),
  showSFootageBrick: Yup.boolean(),
  showSFootageSiding: Yup.boolean(),
  showSFootageMixed: Yup.boolean()
});

export const PlanDetails: FC<IDetails> = ({
  details,
  isLoadingDetails,
  setDirtyForm,
  refetchDetails
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const history = useHistory();
  const { planId }: { planId: string } = useParams();

  const { isLoading: isLoadingPlanStyles, data: planStyles } = useQuery<IPlanStyleRes, Error>(
    ['plan-styles'],
    () => getPlanStyles()
  );

  const { isLoading: isLoadingWallThicknesses, data: wallThicknesses } = useQuery<
    IDropdownResponse[],
    Error
  >(['getWallThicknesses'], () => getWallThicknesses());

  const { isLoading: isLoadingShowerStyles, data: showerStyles } = useQuery<
    IDropdownResponse[],
    Error
  >(['getShowerStyles'], () => getShowerStyles());

  const { isLoading: isLoadingStairStyles, data: stairStyles } = useQuery<
    IDropdownResponse[],
    Error
  >(['getStairStyles'], () => getStairStyles());

  const { isLoading: isLoadingBedBaths, data: bedBaths } = useQuery<IDropdownResponse[], Error>(
    ['getBedBaths'],
    () => getBedBaths()
  );

  return (
    <>
      {isLoadingDetails && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingDetails && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            planId: details?.planId,
            planName: details?.planName,
            cFootage: details?.cFootage ?? 0,
            cFootage1:
              details?.isTwoStory === false
                ? (details.cFootage1 = details.cFootage)
                : details.isTwoStory === true
                ? details.cFootage1
                : 0,
            cFootage2: details?.isTwoStory === true ? details.cFootage2 : 0,
            sFootageBrick: details?.sFootageBrick ?? 0,
            sFootageSiding: details?.sFootageSiding ?? 0,
            sFootageMixed: details?.sFootageMixed ?? 0,
            sFootage1: details?.isTwoStory === true ? details.sFootage1 : 0,
            sFootage2: details?.isTwoStory === true ? details.sFootage2 : 0,
            porchFootage: details?.porchFootage ?? 0,
            garageFootage: details?.garageFootage ?? 0,
            carportFootage: details?.carportFootage ?? 0,
            deckFootage: details?.deckFootage ?? 0,
            unfinishedFootage: details?.unfinishedFootage ?? 0,
            slabStructuralFootage: details?.slabStructuralFootage ?? 0,
            slabNonStructuralFootage: details?.slabNonStructuralFootage ?? 0,
            threeCarCFootage: details?.threeCarCFootage ?? 0,
            threeCarSFootageBrick: details?.threeCarSFootageBrick ?? 0,
            threeCarSFootageMixed: details?.threeCarSFootageMixed ?? 0,
            threeCarSFootageSiding: details?.threeCarSFootageSiding ?? 0,
            threeCarSlabStructuralFootage: details?.threeCarSlabStructuralFootage ?? 0,
            threeCarGarageFootage: details?.threeCarGarageFootage ?? 0,
            planStyle: details?.planStyle ?? '',
            bedBath: details?.bedBath ?? '',
            isTwoStory:
              details?.isTwoStory === true ? 'Yes' : details?.isTwoStory === false ? 'No' : '',
            isMasterUpstairs:
              details?.isMasterUpstairs === true
                ? 'Yes'
                : details?.isMasterUpstairs === false
                ? 'No'
                : '',
            kitchenHasIsland:
              details?.kitchenHasIsland === true
                ? 'Yes'
                : details?.kitchenHasIsland === false
                ? 'No'
                : '',
            stairStyle: details?.stairStyle ?? '',
            showerStyle: details?.showerStyle ?? '',
            wallThickness: details?.wallThickness ?? '',
            salesDetails: details?.salesDetails ?? '',
            widthFeet: details?.widthFeet ?? 0,
            widthInches: details?.widthInches ?? 0,
            depthFeet: details?.depthFeet ?? 0,
            depthInches: details?.depthInches ?? 0,
            ceilingHeightFirstFloor: details?.ceilingHeightFirstFloor ?? 0,
            ceilingHeightSecondFloor: details?.ceilingHeightSecondFloor ?? 0,
            ceilingHeightBasement: details?.ceilingHeightBasement ?? 0,
            showSFootageBrick: details?.showSFootageBrick ?? false,
            showSFootageSiding: details?.showSFootageSiding ?? false,
            showSFootageMixed: details?.showSFootageMixed ?? false
          }}
          validationSchema={DetailsSchema}
          onSubmit={async (values, actions) => {
            try {
              if (values.isTwoStory === 'Yes') {
                values.cFootage = Number(values.cFootage1) + Number(values.cFootage2);
              } else if (values.isTwoStory === 'No') {
                values.cFootage1 = values.cFootage;
              }

              const data: IPlanDetailsPut = {
                ...values,
                isTwoStory: values.isTwoStory === 'Yes' ? true : false,
                isMasterUpstairs: values.isMasterUpstairs === 'Yes' ? true : false,
                kitchenHasIsland: values.kitchenHasIsland === 'Yes' ? true : false,
                cFootage: values.cFootage ?? null,
                cFootage1: values.cFootage1 ?? null,
                cFootage2: values.cFootage2 ?? null,
                sFootageBrick: values.sFootageBrick ?? null,
                sFootageSiding: values.sFootageSiding ?? null,
                sFootageMixed: values.sFootageMixed ?? null,
                sFootage1: values.sFootage1 ?? null,
                sFootage2: values.sFootage2 ?? null,
                porchFootage: values.porchFootage ?? null,
                garageFootage: values.garageFootage ?? null,
                carportFootage: values.carportFootage ?? null,
                deckFootage: values.deckFootage ?? null,
                unfinishedFootage: values.unfinishedFootage ?? null,
                slabStructuralFootage: values.slabStructuralFootage ?? null,
                slabNonStructuralFootage: values.slabNonStructuralFootage ?? null,
                threeCarCFootage: values.threeCarCFootage ?? null,
                threeCarSFootageBrick: values.threeCarSFootageBrick ?? null,
                threeCarSFootageMixed: values.threeCarSFootageMixed ?? null,
                threeCarSFootageSiding: values.threeCarSFootageSiding ?? null,
                threeCarSlabStructuralFootage: values.threeCarSlabStructuralFootage ?? null,
                threeCarGarageFootage: values.threeCarGarageFootage ?? null,
                widthFeet: values.widthFeet ?? null,
                widthInches: values.widthInches ?? null,
                depthFeet: values.depthFeet ?? null,
                depthInches: values.depthInches ?? null,
                ceilingHeightFirstFloor: values.ceilingHeightFirstFloor ?? null,
                ceilingHeightSecondFloor: values.ceilingHeightSecondFloor ?? null,
                ceilingHeightBasement: values.ceilingHeightBasement ?? null
              };
              await putPlanDetails(parseInt(planId), data);
              refetchDetails();
              enqueueSnackbar('Plan has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Plan was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            initialValues,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography className={classes.sqftHeader} variant="h6">
                          Square Footage
                        </Typography>
                        <FormControl component="fieldset" required>
                          <FormLabel>Levels</FormLabel>
                          <RadioGroup
                            sx={{
                              position: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'left',
                              alignItems: 'start'
                            }}
                            aria-label="isTwoStory"
                            name="isTwoStory"
                            value={values.isTwoStory}
                            onChange={e => setFieldValue('isTwoStory', e.target.value)}
                          >
                            <FormControlLabel value={'No'} control={<Radio />} label={'1 Level'} />
                            <FormControlLabel
                              value={'Yes'}
                              control={<Radio />}
                              label={'2 Levels'}
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={3}>
                            {values.isTwoStory === 'Yes' ? (
                              <TextField
                                disabled
                                type="number"
                                fullWidth
                                variant="outlined"
                                autoComplete="nope"
                                label="C-Footage"
                                name="cfootage"
                                size="small"
                                value={values.cFootage}
                                onBlur={handleBlur}
                                onChange={e => setFieldValue('cFootage', e.target.value)}
                              />
                            ) : (
                              <TextField
                                fullWidth
                                type="number"
                                variant="outlined"
                                autoComplete="nope"
                                label="C-Footage"
                                name="cfootage"
                                size="small"
                                value={values.cFootage}
                                onBlur={handleBlur}
                                onChange={e => setFieldValue('cFootage', e.target.value)}
                              />
                            )}
                          </Grid>
                          {values.isTwoStory === 'Yes' && (
                            <>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="C-Footage 1st Level"
                                  name="cfootage1"
                                  size="small"
                                  value={values.cFootage1}
                                  onBlur={e => {
                                    handleBlur(e);
                                    setFieldValue(
                                      'cFootage',
                                      Number(e.target.value) + Number(values.cFootage2)
                                    );
                                  }}
                                  onChange={e => setFieldValue('cFootage1', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="C-Footage 2nd Level"
                                  name="cfootage2"
                                  size="small"
                                  value={values.cFootage2}
                                  onBlur={e => {
                                    handleBlur(e);
                                    setFieldValue(
                                      'cFootage',
                                      Number(e.target.value) + Number(values.cFootage1)
                                    );
                                  }}
                                  onChange={e => setFieldValue('cFootage2', e.target.value)}
                                />
                              </Grid>
                            </>
                          )}
                        </Grid>
                        <Box mt={1}>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              {values.showSFootageBrick && (
                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="All Brick - SFootage"
                                    name="sFootageBrick"
                                    size="small"
                                    value={values.sFootageBrick}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('sFootageBrick', e.target.value)}
                                  />
                                </Grid>
                              )}
                              {values.showSFootageMixed && (
                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Mixed - SFootage"
                                    name="sFootageMixed"
                                    size="small"
                                    value={values.sFootageMixed}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('sFootageMixed', e.target.value)}
                                  />
                                </Grid>
                              )}
                              {values.showSFootageSiding && (
                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="All Siding - SFootage"
                                    name="sFootageSiding"
                                    size="small"
                                    value={values.sFootageSiding}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('sFootageSiding', e.target.value)}
                                  />
                                </Grid>
                              )}
                              {values.isTwoStory === 'Yes' && (
                            <>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="S-Footage 1st Level"
                                  name="sfootage1"
                                  size="small"
                                  value={values.sFootage1}
                                  onBlur={e => {
                                    handleBlur(e);
                                  }}
                                  onChange={e => setFieldValue('sFootage1', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="S-Footage 2nd Level"
                                  name="sfootage2"
                                  size="small"
                                  value={values.sFootage2}
                                  onBlur={e => {
                                    handleBlur(e);
                                  }}
                                  onChange={e => setFieldValue('sFootage2', e.target.value)}
                                />
                              </Grid>
                            </>
                          )}
                            </Grid>
                          </Grid>
                        </Box>
                        <Box mt={1}>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Covered Porch"
                                  name="porchFootage"
                                  size="small"
                                  value={values.porchFootage}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('porchFootage', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Garage"
                                  name="garageFootage"
                                  size="small"
                                  value={values.garageFootage}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('garageFootage', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Carport"
                                  name="carportFootage"
                                  size="small"
                                  value={values.carportFootage}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('carportFootage', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Unfinished Footage"
                                  name="unfinishedFootage"
                                  size="small"
                                  value={values.unfinishedFootage}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('unfinishedFootage', e.target.value)}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box mt={1}>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Slab Structural"
                                  name="slabStructuralFootage"
                                  size="small"
                                  value={values.slabStructuralFootage}
                                  onBlur={handleBlur}
                                  onChange={e =>
                                    setFieldValue('slabStructuralFootage', e.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Slab Non Structural"
                                  name="slabNonStructuralFootage"
                                  size="small"
                                  value={values.slabNonStructuralFootage}
                                  onBlur={handleBlur}
                                  onChange={e =>
                                    setFieldValue('slabNonStructuralFootage', e.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Deck"
                                  name="deckFootage"
                                  size="small"
                                  value={values.deckFootage}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('deckFootage', e.target.value)}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box mt={1}>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="3 Car C-Footage"
                                  name="threeCarCFootage"
                                  size="small"
                                  value={values.threeCarCFootage}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('threeCarCFootage', e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="3 Car Slab Structural"
                                  name="threeCarSlabStructuralFootage"
                                  size="small"
                                  value={values.threeCarSlabStructuralFootage}
                                  onBlur={handleBlur}
                                  onChange={e =>
                                    setFieldValue('threeCarSlabStructuralFootage', e.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="3 Car Garage Footage"
                                  name="threeCarGarageFootage"
                                  size="small"
                                  value={values.threeCarGarageFootage}
                                  onBlur={handleBlur}
                                  onChange={e =>
                                    setFieldValue('threeCarGarageFootage', e.target.value)
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box mt={1}>
                          <Grid item xs={12}>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="3 Car SFootage Brick"
                                  name="threeCarSFootageBrick"
                                  size="small"
                                  value={values.threeCarSFootageBrick}
                                  onBlur={handleBlur}
                                  onChange={e =>
                                    setFieldValue('threeCarSFootageBrick', e.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="3 Car SFootage Mixed"
                                  name="threeCarSFootageMixed"
                                  size="small"
                                  value={values.threeCarSFootageMixed}
                                  onBlur={handleBlur}
                                  onChange={e =>
                                    setFieldValue('threeCarSFootageMixed', e.target.value)
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="3 Car SFootage Siding"
                                  name="threeCarSFootageSiding"
                                  size="small"
                                  value={values.threeCarSFootageSiding}
                                  onBlur={handleBlur}
                                  onChange={e =>
                                    setFieldValue('threeCarSFootageSiding', e.target.value)
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography className={classes.sqftHeader} variant="h6">
                          Plan Attributes
                        </Typography>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="planStyle"
                              label="Style"
                              id="planStyle"
                              variant="outlined"
                              disabled={isLoadingPlanStyles}
                              value={values.planStyle}
                              onChange={({ target: { value } }) => {
                                setFieldValue('planStyle', value);
                              }}
                              onBlur={handleBlur}
                              error={errors && errors.planStyle ? true : false}
                            >
                              {planStyles &&
                                planStyles.records.map((plan, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={plan.name}>
                                      {plan.name}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched && touched.planStyle && errors && errors.planStyle && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="bedBath"
                              label="Bed/Bath"
                              variant="outlined"
                              id="bedBath"
                              disabled={isLoadingBedBaths}
                              value={values.bedBath}
                              onChange={({ target: { value } }) => {
                                setFieldValue('bedBath', value);
                              }}
                              onBlur={handleBlur}
                              error={errors && errors.bedBath ? true : false}
                            >
                              {bedBaths &&
                                bedBaths.map((bed, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={bed.value}>
                                      {bed.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched && touched.bedBath && errors && errors.bedBath && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="showerStyle"
                              label="Shower Style"
                              id="showerStyle"
                              variant="outlined"
                              disabled={isLoadingShowerStyles}
                              value={values.showerStyle}
                              onChange={({ target: { value } }) => {
                                setFieldValue('showerStyle', value);
                              }}
                              onBlur={handleBlur}
                              error={errors && errors.showerStyle ? true : false}
                            >
                              {showerStyles &&
                                showerStyles.map((shower, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={shower.value}>
                                      {shower.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched && touched.showerStyle && errors && errors.showerStyle && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined" size="small">
                              <SMBSelect
                                name="wallThickness"
                                label="Wall Thickness"
                                id="wallThickness"
                                variant="outlined"
                                disabled={isLoadingWallThicknesses}
                                value={values.wallThickness}
                                onChange={({ target: { value } }) => {
                                  setFieldValue('wallThickness', value);
                                }}
                              >
                                {wallThicknesses &&
                                  wallThicknesses.map((wall, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={wall.value}>
                                        {wall.description}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Width Feet"
                              name="widthFeet"
                              size="small"
                              value={values.widthFeet}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('widthFeet', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Width Inches"
                              name="widthInches"
                              size="small"
                              value={values.widthInches}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('widthInches', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Depth Feet"
                              name="depthFeet"
                              size="small"
                              value={values.depthFeet}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('depthFeet', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Depth Inches"
                              name="depthInches"
                              size="small"
                              value={values.depthInches}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('depthInches', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Ceiling Height First Floor"
                              name="ceilingHeightFirstFloor"
                              size="small"
                              value={values.ceilingHeightFirstFloor}
                              onBlur={handleBlur}
                              onChange={e =>
                                setFieldValue('ceilingHeightFirstFloor', e.target.value)
                              }
                              error={
                                touched.ceilingHeightFirstFloor && !!errors.ceilingHeightFirstFloor
                              }
                              helperText={
                                touched.ceilingHeightFirstFloor && errors.ceilingHeightFirstFloor
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Ceiling Height Second Floor"
                              name="ceilingHeightSecondFloor"
                              size="small"
                              value={values.ceilingHeightSecondFloor}
                              onBlur={handleBlur}
                              onChange={e =>
                                setFieldValue('ceilingHeightSecondFloor', e.target.value)
                              }
                              error={
                                touched.ceilingHeightSecondFloor &&
                                !!errors.ceilingHeightSecondFloor
                              }
                              helperText={
                                touched.ceilingHeightSecondFloor && errors.ceilingHeightSecondFloor
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Ceiling Height Basement"
                              name="ceilingHeightBasement"
                              size="small"
                              value={values.ceilingHeightBasement}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('ceilingHeightBasement', e.target.value)}
                              error={
                                touched.ceilingHeightBasement && !!errors.ceilingHeightBasement
                              }
                              helperText={
                                touched.ceilingHeightBasement && errors.ceilingHeightBasement
                              }
                            />
                          </Grid>
                          {values.isTwoStory === 'Yes' && (
                            <Grid item xs={12} sm={4}>
                              <FormControl
                                fullWidth
                                variant="outlined"
                                size="small"
                                className={classes.stairField}
                              >
                                <SMBSelect
                                  name="stairStyle"
                                  label="Stair Style"
                                  variant="outlined"
                                  handleClear={() => setFieldValue('stairStyle', '')}
                                  id="stairStyle"
                                  disabled={isLoadingStairStyles}
                                  value={values.stairStyle}
                                  onChange={({ target: { value } }) => {
                                    setFieldValue('stairStyle', value);
                                  }}
                                >
                                  {stairStyles &&
                                    stairStyles.map((stair, index) => {
                                      return (
                                        <MenuItem key={`${index}`} value={stair.value}>
                                          {stair.description}
                                        </MenuItem>
                                      );
                                    })}
                                </SMBSelect>
                              </FormControl>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={4}>
                            <FormControl component="fieldset">
                              <FormLabel>Kitchen has island</FormLabel>
                              <RadioGroup
                                sx={{
                                  position: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'left',
                                  alignItems: 'start'
                                }}
                                aria-label="kitchenHasIsland"
                                name="kitchenHasIsland"
                                value={values.kitchenHasIsland}
                                onChange={e => setFieldValue('kitchenHasIsland', e.target.value)}
                              >
                                <FormControlLabel value={'Yes'} control={<Radio />} label={'Yes'} />
                                <FormControlLabel value={'No'} control={<Radio />} label={'No'} />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                          {values.isTwoStory === 'Yes' && (
                            <Grid item xs={12} sm={4}>
                              <FormControl component="fieldset">
                                <FormLabel>Master is on Second Level</FormLabel>
                                <RadioGroup
                                  sx={{
                                    position: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'left',
                                    alignItems: 'start'
                                  }}
                                  aria-label="isMasterUpstairs"
                                  name="isMasterUpstairs"
                                  value={values.isMasterUpstairs}
                                  onChange={e => setFieldValue('isMasterUpstairs', e.target.value)}
                                >
                                  <FormControlLabel
                                    value={'Yes'}
                                    control={<Radio />}
                                    label={'Yes'}
                                  />
                                  <FormControlLabel value={'No'} control={<Radio />} label={'No'} />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={6}
                              variant="outlined"
                              autoComplete="nope"
                              label="Sales Details"
                              name="Sales Details"
                              size="small"
                              value={values.salesDetails}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('salesDetails', e.target.value)}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={planId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/plans/manage-plans');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  pictureImages: {
    marginRight: theme.spacing(1),
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  colorImage: {
    width: 50,
    height: 50
  },
  sqftHeader: {
    paddingBottom: theme.spacing(1)
  },
  stairField: {
    paddingRight: theme.spacing(4)
  }
}));
